import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { studio } from '@/mocks/projects';
import { goToHomeTop, useGoToHomeSection } from '@/lib/home-nav';
import { playRouteFade } from '@/lib/route-fade';

type NavItem = { label: string; hash?: string; to?: string };

const links: NavItem[] = [
  { label: 'Work', hash: 'work' },
  { label: 'Studio', to: '/studio' },
  { label: 'Services', hash: 'services' },
  { label: 'Contact', to: '/contact' },
];

const linkCls = 'text-[13px] font-medium text-foreground-950 whitespace-nowrap cursor-pointer';

export default function SiteNav() {
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const goToHomeSection = useGoToHomeSection();

  useEffect(() => { setOpen(false); }, [pathname]);

  const renderLink = (l: NavItem) => {
    if (l.to) {
      return (
        <NavLink key={l.label} to={l.to} onClick={playRouteFade} className={({ isActive }) => `${linkCls} mo-uline ${isActive ? 'is-active' : ''}`}>{l.label}</NavLink>
      );
    }
    return (
      <a key={l.label} href={`/#${l.hash}`} className={`${linkCls} mo-uline`} onClick={(e) => { e.preventDefault(); setOpen(false); playRouteFade(); goToHomeSection(l.hash as string); }}>{l.label}</a>
    );
  };

  const themeBtn = (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      className={`${linkCls} border-l border-background-200 pl-6 transition-opacity hover:opacity-70`}
    >
      <span className={theme === 'light' ? 'text-foreground-950' : 'text-foreground-500'}>Light</span>
      <span className="mx-1 text-foreground-500">/</span>
      <span className={theme === 'dark' ? 'text-foreground-950' : 'text-foreground-500'}>Dark</span>
    </button>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur-sm transition-colors">
      <nav className="flex h-16 items-center justify-between px-4 md:px-10" aria-label="Primary">
        <Link to="/" onClick={(e) => { playRouteFade(); if (pathname === '/') { e.preventDefault(); goToHomeTop(); } }} className="flex items-baseline gap-2 whitespace-nowrap cursor-pointer">
          <span className="font-heading text-[15px] font-semibold tracking-[-0.01em] text-foreground-950">{studio.name}</span>
          <span className="hidden font-label text-[11px] font-medium uppercase tracking-[0.12em] text-foreground-500 lg:inline">Photography</span>
        </Link>
        <div className="hidden items-center gap-7 md:flex">
          {links.map((l) => renderLink(l))}
          {themeBtn}
          <Link to="/contact" onClick={playRouteFade} className="mo-btn mo-btn--nav whitespace-nowrap bg-foreground-950 px-4 py-2 text-[13px] font-medium text-background-50 hover:opacity-[0.88] cursor-pointer"><span className="mo-label">Book a shoot</span></Link>
        </div>
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center text-foreground-950 md:hidden cursor-pointer"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((o) => !o)}
        >
          <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </nav>
      {open && (
        <div id="mobile-menu" className="border-t border-background-200 bg-background-50 px-4 py-6 md:hidden">
          <div className="flex flex-col items-start gap-5">
            {links.map((l) => renderLink(l))}
            <div className="pt-2 [&>button]:border-l-0 [&>button]:pl-0">{themeBtn}</div>
          </div>
        </div>
      )}
    </header>
  );
}